import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radio, RefreshCw, TrendingUp, TrendingDown, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadCsv, csvButtonBase } from "@/lib/csv";

const UNIVERSES = [
  { id: "bist30", label: "BIST 30" },
  { id: "bist100", label: "BIST 100" },
  { id: "bist_all", label: "Tüm BIST" },
];

export default function SignalsPage() {
  const [universe, setUniverse] = useState("bist30");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const load = async (u = universe) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/signals?universe=${u}&limit=80`);
      setData(data);
    } catch { toast.error("Sinyaller yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(universe); }, [universe]);

  return (
    <div className="space-y-6" data-testid="signals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
            <Radio size={22} className="text-signal-up" /> Sinyal Akışı
          </h1>
          <p className="text-sm text-text-secondary mt-1">Son seansta kesişim, kırılım ve hacim patlaması yaşayan hisseler</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-bg-panel p-1 rounded-lg border border-bg-border">
            {UNIVERSES.map((u) => (
              <button
                key={u.id}
                data-testid={`signals-universe-${u.id}`}
                onClick={() => setUniverse(u.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  universe === u.id ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"
                }`}
              >{u.label}</button>
            ))}
          </div>
          <button
            data-testid="signals-csv"
            onClick={() => {
              if (!data?.items?.length) return;
              const rows = data.items.map((it) => ({
                symbol: it.symbol, name: it.name,
                close: it.close, pct_change: it.pct_change,
                signals: (it.signals || []).map((s) => s.label).join(" | "),
              }));
              downloadCsv("ztan_sinyaller.csv", rows, ["symbol","name","close","pct_change","signals"]);
            }}
            className={csvButtonBase()}
          ><Download size={12} /> CSV</button>
          <button
            data-testid="signals-refresh-btn"
            onClick={() => load()}
            className="bg-bg-hover text-text-primary border border-bg-border px-3 py-1.5 rounded-lg text-xs hover:bg-bg-border flex items-center gap-1.5"
          ><RefreshCw size={12} /> Yenile</button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 flex justify-center"><div className="radar-loader" /></div>
      ) : !data || data.items.length === 0 ? (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center">
          <Radio size={28} className="text-text-secondary mx-auto mb-3" />
          <h3 className="text-text-primary font-semibold mb-1">Henüz sinyal yok</h3>
          <p className="text-sm text-text-secondary">Piyasa sakin — güncel seans verilerini bekliyoruz.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((it) => (
            <div
              key={it.symbol}
              data-testid={`signal-card-${it.symbol}`}
              onClick={() => nav(`/stock/${it.symbol}`)}
              className="bg-bg-panel border border-bg-border hover:border-brand-blue/60 rounded-xl p-4 cursor-pointer transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-lg font-mono font-bold text-text-primary tracking-wider">{it.symbol}</div>
                  <div className="text-[11px] text-text-secondary mt-0.5">{it.name}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono text-text-primary">{fmt(it.close)}₺</div>
                  <div className={`text-xs font-mono flex items-center gap-1 justify-end ${
                    (it.pct_change ?? 0) >= 0 ? "text-signal-up" : "text-signal-down"
                  }`}>
                    {(it.pct_change ?? 0) >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                    {fmtPct(it.pct_change)}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {it.signals.map((s, i) => (
                  <span
                    key={i}
                    className={`text-[10px] px-2 py-0.5 rounded border ${
                      s.tone === "up"
                        ? "text-signal-up bg-signal-up/10 border-signal-up/30"
                        : "text-signal-down bg-signal-down/10 border-signal-down/30"
                    }`}
                  >{s.label}</span>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-bg-border flex items-center justify-end text-xs text-brand-blueLight opacity-0 group-hover:opacity-100 transition-opacity">
                Detay <ChevronRight size={12} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmt(v, d = 2) { if (v == null) return "-"; return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d }); }
function fmtPct(v) { if (v == null) return "-"; return `${v > 0 ? "+" : ""}${Number(v).toFixed(2)}%`; }
